/**
 * How long a pack's credits last when it is sold with a count of its own
 * (custom-pack-validity-at-sale, 2026-10-30).
 *
 * A staff-priced custom pack ("12 studio credits") is granted on a CARRIER
 * slug — `single-session` for the studio, `single-home-session` for home —
 * whose catalogue entry is a one-credit pack. Its expiry used to be that
 * carrier's validity whatever the count, so a 12-credit pack lasted the
 * 30 days of one session. The size rule here gives such a pack the validity
 * of the catalogue pack of its size instead:
 *
 *   - a count within the offering's own size keeps the offering's own
 *     validity, exactly as a catalogue sale always has;
 *   - a bigger count takes the smallest same-`session_type` pack that holds
 *     it, or, past the largest such pack, the largest one;
 *   - never less than the offering's own validity, and a chosen pack with no
 *     validity means the credits never expire.
 *
 * With the deployed E-Pulsive catalogue: studio 1 credit → 30 days, 2–12 →
 * 90, 13+ → 365; home → 365 at any count.
 *
 * ONE implementation, two readers: `grant_member_package` and
 * `record_manual_sale` apply it when the sale names no `validity_days`, and
 * the dashboard's "Valid for (days)" box pre-fills from it, so what staff see
 * is what the grant would do. Pure and dependency-free, so the SPA can import
 * it (`l2-yaml.ts` reads the file system and cannot be).
 */

/** The bounds every `validity_days` input schema uses (1 day to 10 years). */
export const PACKAGE_VALIDITY_DAYS_MIN = 1;
export const PACKAGE_VALIDITY_DAYS_MAX = 3650;

/**
 * The three facts of a catalogue pack the rule reads. A `PackageOffering`
 * from the L2 YAML satisfies it, and so does the dashboard's package lookup.
 */
export interface PackageValidityOffering {
  readonly sessions_total: number;
  readonly validity_days?: number;
  readonly session_type?: string;
}

/** A pack's validity in days, `null` when it never expires. */
function validityOf(o: PackageValidityOffering): number | null {
  return typeof o.validity_days === "number" && o.validity_days > 0
    ? o.validity_days
    : null;
}

/** The longer of two validities, `null` (never expires) being the longest. */
function moreGenerous(a: number | null, b: number | null): number | null {
  if (a === null || b === null) return null;
  return Math.max(a, b);
}

/**
 * The validity, in days, of `offering` sold as `sessions` credits; `null`
 * when those credits never expire. `offerings` is the studio's package
 * catalogue; only packs of the offering's own `session_type` are read.
 */
export function resolvePackageValidityDays(
  offerings: ReadonlyArray<PackageValidityOffering>,
  offering: PackageValidityOffering,
  sessions: number,
): number | null {
  const own = validityOf(offering);
  if (sessions <= offering.sessions_total || own === null) return own;

  const sessionType = offering.session_type ?? null;
  const sameType = offerings.filter((o) => (o.session_type ?? null) === sessionType);
  const holding = sameType.filter((o) => o.sessions_total >= sessions);
  const pool = holding.length > 0 ? holding : sameType;
  if (pool.length === 0) return own;

  // The smallest pack that holds the count, else the largest pack there is.
  const size =
    holding.length > 0
      ? Math.min(...pool.map((o) => o.sessions_total))
      : Math.max(...pool.map((o) => o.sessions_total));
  // Two packs of that size: the more generous validity decides, whatever the
  // catalogue's order.
  const chosen = pool
    .filter((o) => o.sessions_total === size)
    .map(validityOf)
    .reduce<number | null>((best, v) => moreGenerous(best, v), 0);
  return moreGenerous(chosen, own);
}
